import React from 'react';
import { useSelector } from 'react-redux';
import { RailSidebar } from './RailSidebar';
import { useRailBadges } from './useRailBadges';
import { initialsOf } from '@/components/ui';

interface AppShellProps {
  history: { push: (path: string) => void; location: { pathname: string } };
  onLogout: () => void;
  children: React.ReactNode;
}

export const AppShell = ({ history, onLogout, children }: AppShellProps) => {
  // Guard against loggedUser.data being undefined on first render (initial state is {} not undefined,
  // but the server may return { status: false, reason: '...' } if consents are missing).
  const data = useSelector((s: any) => s.loggedUser?.data) || {};

  const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();

  // Role display: loggedUser.data has user_role (join array with role.roleType/role.role).
  // data.role does NOT exist as a top-level field — use user_role[0].role.role when available,
  // fall back to data.userType ('Internal' | 'External').
  const roleDisplay: string =
    Array.isArray(data.user_role) && data.user_role[0]?.role?.role
      ? data.user_role[0].role.role
      : data.userType || '';

  const user = {
    initials: initialsOf(fullName || data.emailAddress || '?'),
    name: fullName || data.emailAddress || '',
    role: roleDisplay,
    isExternal: data.userType === 'External',
  };

  const activePath = history.location.pathname || '/';

  // Role-gated nav (e.g. Admin Reports) reads capability flags from the /me slice —
  // BE decides, FE renders. Default-deny: {} until /me resolves.
  const permissions = useSelector((s: any) => s.me?.permissions) || {};

  // Live unread counts for the rail badges, keyed by NAV_ITEMS id.
  const { messages, notifications } = useRailBadges();
  const counts: Record<string, number> = { messages, notifications };

  return (
    <div className="tw-flex tw-flex-row tw-h-screen tw-w-full tw-overflow-hidden">
      <RailSidebar
        activePath={activePath}
        onNavigate={(path) => history.push(path)}
        onLogout={onLogout}
        user={user}
        counts={counts}
        permissions={permissions}
      />
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-h-full">
        {/* Topbar removed — its items (+ / timekeep / bell / feedback / user identity)
            will be relocated. Topbar.tsx kept on disk as the donor for that move. */}
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto tw-bg-white">
          {children}
        </div>
      </div>
    </div>
  );
};
